import { LogLevel, LogService } from "./logging";
import {
  initiateOrdersSync,
  initiatePosFilesSync,
  initiateReceiptCountersSync,
  initiateTransactionsSync,
  resetAllSyncLocks,
} from "@/services/sync-service";
import { dexieDB } from "./dexie-database";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { upsertUntypedBooking } from "@/db/untyped-booking";
import { purgeExpiredTimeslots } from "@/dexie/timeslots";
import { checkAndRunMigrations } from "@/services/migration";

export async function setupServices() {
  // Ensure Dexie DB is open before using it
  try {
    if (!dexieDB.isOpen()) {
      await dexieDB.open();
    }
    // clean up stale bookings and transactions (stuck in InProgress from crash)
    await resetStaleBookings();
    await resetStaleTransactions();
    // purge timeslots that ended before today to prevent unbounded accumulation
    await purgeExpiredTimeslots();
  } catch (error) {
    LogService.info("Error during Dexie DB setup", error, LogLevel.Error);
  }

  // Run any pending data migrations
  await checkAndRunMigrations();

  // Setup the sync job triggers
  setupSyncTriggers();
}

/**
 * Function to setup sync triggers
 * This function is called once on app startup
 */
function setupSyncTriggers() {
  // Reset any stale sync locks from previous session (e.g., crash while syncing)
  resetAllSyncLocks();

  const syncIntervalMs = 2 * 60 * 1000;
  setInterval(initiateOrdersSync, syncIntervalMs);
  setInterval(initiatePosFilesSync, syncIntervalMs);
  setInterval(initiateReceiptCountersSync, syncIntervalMs);
  setInterval(initiateTransactionsSync, syncIntervalMs);

  // Trigger all sync jobs when the app is back online
  window.addEventListener("online", initiateOrdersSync);
  window.addEventListener("online", initiatePosFilesSync);
  window.addEventListener("online", initiateReceiptCountersSync);
  window.addEventListener("online", initiateTransactionsSync);
}

/**
 * Reset any bookings stuck in InProgress sync state back to Pending
 * This handles crashes or force-closes while syncing
 */
async function resetStaleBookings() {
  try {
    // Double-check database is open
    if (!dexieDB.isOpen()) {
      LogService.info("Dexie DB not open, skipping stale booking reset");
      return;
    }

    // Reset untyped bookings
    const staleUntypedBookings = await dexieDB.untypedBookings
      .where("syncStatus")
      .equals(SyncStatus.InProgress)
      .toArray();

    if (staleUntypedBookings.length) {
      LogService.info(
        `Found ${staleUntypedBookings.length} stale untyped bookings`
      );
    }

    for (const booking of staleUntypedBookings) {
      LogService.info(
        `Sync: Going to mark booking ${booking.uuid} back to Pending`
      );
      try {
        booking.syncStatus = SyncStatus.Pending;
        await upsertUntypedBooking(booking);
      } catch (error) {
        LogService.info(
          `Sync: Error marking booking ${booking.uuid} back to Pending`,
          error,
          LogLevel.Error
        );
      }
    }
  } catch (error) {
    LogService.info("Error resetting stale bookings", error, LogLevel.Error);
  }
}

/**
 * Reset any transactions stuck in InProgress sync state back to Pending
 * This handles crashes or force-closes while syncing
 */
async function resetStaleTransactions() {
  try {
    if (!dexieDB.isOpen()) return;

    const staleTransactions = await dexieDB.untypedTransactions
      .where("syncStatus")
      .equals(SyncStatus.InProgress)
      .toArray();

    if (!staleTransactions.length) return;

    LogService.info(
      `Found ${staleTransactions.length} stale transactions, resetting to Pending`
    );

    for (const tx of staleTransactions) {
      try {
        tx.syncStatus = SyncStatus.Pending;
        await dexieDB.untypedTransactions.put(tx);
      } catch (error) {
        LogService.info(
          `Sync: Error marking transaction ${tx.uuid} back to Pending`,
          error,
          LogLevel.Error
        );
      }
    }
  } catch (error) {
    LogService.info(
      "Error resetting stale transactions",
      error,
      LogLevel.Error
    );
  }
}
